import { useEffect, useState, type ReactNode } from 'react';

interface SocialAvatarProps {
  /** Stored picture URL. May be null/blank, or a Meta CDN URL that has expired. */
  src?: string | null;
  alt?: string;
  /** Applied to the <img> only — the fallback keeps its own classes. */
  className?: string;
  /**
   * Rendered whenever there is no usable image: `src` is blank, OR the browser
   * failed to load it. Pass the SAME placeholder the call site already showed
   * for an account with no picture, so a dead URL degrades to a state the user
   * already recognises instead of a broken-image box.
   */
  fallback: ReactNode;
  loading?: 'lazy' | 'eager';
}

/**
 * Avatar image for a social profile picture (Instagram account, Facebook Page,
 * WhatsApp/IG contact) that may be served from Meta's own CDN.
 *
 * WHY THIS EXISTS — `ig_profile_picture_url` / `fb_page_picture_url` /
 * `profile_picture_url` are **signed, expiring** `cdninstagram.com` /
 * `fbcdn.net` URLs. They carry an `oe=<hex unix seconds>` expiry and Meta's edge
 * returns **HTTP 403** once it passes (verified on real rows: URLs stored in
 * April and July 2026 both 403'd on 21 Aug 2026). Meta can also reject a
 * perfectly unexpired URL on hotlink/referrer grounds. Every site that rendered
 * these through a bare `<img>` therefore showed the browser's default
 * broken-image box — the symptom reported across the inbox header, the account
 * switchers, the social-media posts pages and the CRM lists.
 *
 * Radix `Avatar` + `AvatarFallback` already degrades correctly on a load error,
 * which is exactly why those call sites never broke. This component gives the
 * plain-`<img>` sites the same two guarantees:
 *   - `referrerPolicy="no-referrer"` so Meta's hotlink check has nothing to
 *     reject (the same workaround already used by `RemoteCdnImage` and the
 *     YouTube/TikTok thumbnails)
 *   - an `onError` fallback, so a dead URL renders the call site's own
 *     placeholder rather than a broken image
 *
 * The backend separately self-heals the stored URL (see
 * `backend/src/services/socialProfilePictureRefresh.js`); this is the layer that
 * keeps the UI honest when that refresh cannot succeed — a revoked or expired
 * long-lived token means no fresh URL exists to fetch.
 */
export function SocialAvatar({
  src,
  alt = '',
  className,
  fallback,
  loading = 'lazy',
}: SocialAvatarProps) {
  const [failed, setFailed] = useState(false);

  // A new src (the backend refresh landing, or switching to another account in
  // the same list position) deserves a fresh attempt — without this, one dead
  // URL would pin the fallback for every later account rendered here.
  useEffect(() => {
    setFailed(false);
  }, [src]);

  const usable = typeof src === 'string' && src.trim().length > 0;
  if (!usable || failed) return <>{fallback}</>;

  return (
    <img
      src={src as string}
      alt={alt}
      className={className}
      loading={loading}
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  );
}
